import React from 'react';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { cx } from '../../../utils/cx';
import styles from './ToastProvider.module.scss';

export type ToastVariant = 'info' | 'success' | 'error' | 'warning';

type Notify = (message: string, variant?: ToastVariant) => void;

const ToastContext = createContext<Notify | null>(null);

const VISIBLE_MS = 2200;
const ERROR_VISIBLE_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
    const [message, setMessage] = useState('');
    const [variant, setVariant] = useState<ToastVariant>('info');
    const [visible, setVisible] = useState(false);
    const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const notify = useCallback<Notify>((text, nextVariant = 'info') => {
        setMessage(text);
        setVariant(nextVariant);
        setVisible(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(
            () => setVisible(false),
            nextVariant === 'error' || nextVariant === 'warning' ? ERROR_VISIBLE_MS : VISIBLE_MS,
        );
    }, []);

    useEffect(() => () => clearTimeout(timer.current), []);

    const isError = variant === 'error';

    return (
        <ToastContext.Provider value={notify}>
            {children}
            <div
                className={cx(styles.toast, visible && styles.show, styles[variant])}
                role={isError ? 'alert' : 'status'}
                aria-live={isError ? 'assertive' : 'polite'}
            >
                {message}
            </div>
        </ToastContext.Provider>
    );
}

export function useToast(): Notify {
    const notify = useContext(ToastContext);
    if (!notify) throw new Error('useToast должен вызываться внутри <ToastProvider>');
    return notify;
}